import { useEffect, useState, useCallback, useRef } from "react";
import "./ricerche.css";
import api from "../api";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const Ricerche = () => {
  const [loading, setLoading] = useState(false);
  const [ricerches, setRicerches] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  const fetchRicerche = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const filterParams = buildFilterQueryParams(
          filtersParam,
          filterFnsParam
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get("/ricerche", {
          params,
        });

        if (response.status === 200) {
          const { content = [], page: pageInfo } = response.data ?? {};
          
          setRicerches(content);
          setTotalRows(pageInfo?.totalElements ?? content.length);
        }
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per visualizzare le ricerche!");

      navigate("/login");
      localStorage.clear();
    } else {
      fetchRicerche();
    }
  }, [navigate, userRole, fetchRicerche]);

  const columns = [
    { field: "sede", header: "Sede" },
    { field: "titoloCorso", header: "Titolo Corso" },
    { field: "titoloModulo", header: "Titolo Modulo" },
    { field: "ore", header: "Ore" },
    { field: "mansione", header: "Mansione" },
    { field: "statoRicerca", header: "Stato Ricerca" },
    { field: "docentiAssociati", header: "Docenti Associati" },
  ];

  const handleRowClick = (ricerche) => {
    navigate(`/ricerca/${ricerche.id}`);
  };

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/ricerche", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `ricerche.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Ricerche</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-content ricerche">
        {ricerches && (
          <Table
            columns={columns}
            data={ricerches}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            totalRows={totalRows}
            perPage={pageSize}
            currentPage={page + 1}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFiltersChange}
            columnFilterFns={columnFilterFns}
            onColumnFilterFnsChange={handleColumnFilterFnsChange}
          />
        )}
      </div>
    </div>
  );
};

export default Ricerche;
